import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './CreatePostModal.module.css';

function CreatePostModal({ onClose, onSuccess }) {
  const { currentUser } = useAuth();
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 2);
    setImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setImages(previews);
  };

  const handleRemoveImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim() && images.length === 0) {
      setError('Veuillez entrer du texte ou ajouter au moins une image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let imageUrls = [];
      if (imageFiles.length > 0) {
        imageUrls = await Promise.all(
          imageFiles.map(async (file) => {
            const imageRef = ref(storage, `posts/${currentUser.uid}/${Date.now()}_${file.name}`);
            await uploadBytes(imageRef, file);
            return getDownloadURL(imageRef);
          }),
        );
      }

      await addDoc(collection(db, 'posts'), {
        userId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.email?.split('@')[0],
        text: text.trim(),
        images: imageUrls,
        likesCount: 0,
        commentsCount: 0,
        createdAt: serverTimestamp(),
      });

      onSuccess();
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating post:', err);
      setError('Une erreur est survenue lors de la création de la question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Poser une question</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.userInfo}>
            <div className={styles.avatar}>
              {currentUser?.displayName?.[0] || currentUser?.email?.[0] || 'U'}
            </div>
            <span className={styles.userName}>
              {currentUser?.displayName || currentUser?.email?.split('@')[0]}
            </span>
          </div>

          <div className={styles.formGroup}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={300}
              rows={4}
              className={styles.textarea}
              placeholder="Quelle est votre question ?"
              autoFocus
            />
            <div className={styles.charCount}>{text.length}/300</div>
          </div>

          {images.length > 0 && (
            <div className={styles.imagePreviews}>
              {images.map((preview, index) => (
                <div key={index} className={styles.imagePreview}>
                  <img src={preview} alt={`Preview ${index + 1}`} />
                  <button
                    type="button"
                    className={styles.removeImage}
                    onClick={() => handleRemoveImage(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <label className={styles.imageButton}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className={styles.fileInput}
                disabled={images.length >= 2}
              />
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z"/>
              </svg>
              <span>Photo</span>
              {images.length >= 2 && <span className={styles.maxReached}>(max atteint)</span>}
            </label>
          </div>

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={loading || (!text.trim() && images.length === 0)}
          >
            {loading ? 'Publication...' : 'Publier'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreatePostModal;

